import { useState, useMemo, useCallback, useEffect } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts'
import { ChevronRight } from 'lucide-react'
import SectionCard from './SectionCard'
import { govdataAPI } from '../../services/govdata'
import type { AcqPerformanceRollup, HierarchyLevel } from '../../types/govdata'
import { formatDollars, formatNumber } from '../../types/govdata'

export type DrillMetric = 'speed' | 'vehicles' | 'small_biz' | 'ot_prevalence'

const LEVEL_LABELS: Record<HierarchyLevel, string> = {
  agency: 'Departments',
  macom: 'Major Commands (MAJCOM)',
  subcom1: 'Sub-Commands',
  office: 'Offices',
}

const NEXT_LEVEL: Record<HierarchyLevel, HierarchyLevel | null> = {
  agency: 'macom',
  macom: 'subcom1',
  subcom1: 'office',
  office: null,
}

const ENTITY_COLORS = [
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
  '#ec4899', '#f43f5e', '#ef4444', '#f97316', '#eab308',
  '#22c55e', '#14b8a6', '#06b6d4', '#0ea5e9', '#64748b',
]

const VEHICLE_COLORS: Record<string, string> = {
  'GWAC/IDIQ': '#22c55e',
  'BPA': '#3b82f6',
  'Standalone': '#64748b',
  'OT Agreement': '#7c3aed',
  'OT IDV': '#a855f7',
  'OT Order': '#d946ef',
}

const OT_COLORS = {
  ot_agreement: '#8b5cf6',
  ot_idv: '#a855f7',
  ot_order: '#d946ef',
  far: '#94a3b8',
}

interface BreadcrumbEntry {
  level: HierarchyLevel
  code: string | undefined
  name: string
}

interface Props {
  metric: DrillMetric
  fiscalYear?: number
}

const METRIC_CONFIG: Record<DrillMetric, {
  title: string
  subtitle: (level: string) => string
  sortBy: string
}> = {
  speed: {
    title: 'Speed to Contract',
    // Note: avg_days_to_award was removed in PR #91 (period_of_performance_start_date
    // was usually equal to date_signed in FPDS). The /acq-dashboard route still has a
    // 'speed' metric option but the real percentile-based speed-to-contract data
    // lives at /exec/dashboard via the speed_to_contract mart. This view sorts by
    // award volume as a stand-in.
    subtitle: (level) => `Comparing ${level.toLowerCase()} — by award volume (real speed data on /exec/dashboard)`,
    sortBy: '-total_awards',
  },
  vehicles: {
    title: 'Contract Vehicles',
    subtitle: (level) => `Comparing ${level.toLowerCase()} — awards by vehicle type`,
    sortBy: '-total_awards',
  },
  small_biz: {
    title: 'Small Business Engagement',
    subtitle: (level) => `Comparing ${level.toLowerCase()} — small business rate`,
    sortBy: '-small_biz_pct',
  },
  ot_prevalence: {
    title: 'Other Transaction Prevalence',
    subtitle: (level) => `Comparing ${level.toLowerCase()} — OT vs FAR award mix`,
    sortBy: '-total_ot_awards',
  },
}

export default function HierarchyDrillChart({ metric, fiscalYear }: Props) {
  const [data, setData] = useState<AcqPerformanceRollup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbEntry[]>([
    { level: 'agency', code: undefined, name: 'All' },
  ])

  const currentLevel = breadcrumbs[breadcrumbs.length - 1]
  const nextLevel = NEXT_LEVEL[currentLevel.level]
  const queryLevel = nextLevel || currentLevel.level

  const config = METRIC_CONFIG[metric]

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, any> = {
        hierarchy_level: queryLevel,
        sort_by: config.sortBy,
      }
      if (currentLevel.code) params.parent_code = currentLevel.code
      if (fiscalYear) params.fiscal_year = fiscalYear
      const resp = await govdataAPI.acqPerformance(params)
      setData(resp.results)
    } catch (e: any) {
      setError(e.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [queryLevel, currentLevel.code, fiscalYear, config.sortBy])

  useEffect(() => { fetchData() }, [fetchData])

  // Reset breadcrumbs when metric changes
  useEffect(() => {
    setBreadcrumbs([{ level: 'agency', code: undefined, name: 'All' }])
  }, [metric])

  const canDrill = nextLevel !== null && NEXT_LEVEL[nextLevel] !== null

  const handleBarClick = (_: any, idx: number) => {
    if (!canDrill) return
    const entry = chartItems[idx]
    if (!entry) return
    setBreadcrumbs(prev => [
      ...prev,
      { level: nextLevel!, code: entry.code, name: entry.label },
    ])
  }

  const handleBreadcrumbClick = (idx: number) => {
    setBreadcrumbs(prev => prev.slice(0, idx + 1))
  }

  // Build chart items based on metric — shape varies per metric
  const chartItems: any[] = useMemo(() => {
    const filtered = data.filter(r => r.total_awards > 0)

    if (metric === 'speed') {
      // See METRIC_CONFIG.speed comment: this view now ranks by award volume
      // since the avg_days_to_award metric was deleted in PR #91. Real
      // percentile speed-to-contract data is at /exec/dashboard.
      return filtered
        .sort((a, b) => (b.total_awards ?? 0) - (a.total_awards ?? 0))
        .slice(0, 20)
        .map(r => ({
          code: r.entity_code,
          label: r.entity_name || r.entity_code,
          value: r.total_awards,
          total_awards: r.total_awards,
          child_count: r.child_count,
        }))
    }

    if (metric === 'small_biz') {
      return filtered
        .sort((a, b) => (b.small_biz_pct ?? 0) - (a.small_biz_pct ?? 0))
        .slice(0, 20)
        .map(r => ({
          code: r.entity_code,
          label: r.entity_name || r.entity_code,
          value: Math.round((r.small_biz_pct ?? 0) * 1000) / 10,
          total_awards: r.total_awards,
          small_biz_awards: r.small_biz_awards,
          child_count: r.child_count,
        }))
    }

    if (metric === 'vehicles') {
      return filtered
        .sort((a, b) => b.total_awards - a.total_awards)
        .slice(0, 20)
        .map(r => ({
          code: r.entity_code,
          label: r.entity_name || r.entity_code,
          gwac_idiq: (r.gwac_idiq_count ?? 0),
          bpa: (r.bpa_count ?? 0),
          standalone: (r.standalone_contract_count ?? 0),
          ot_agreement: (r.ot_agreement_count ?? 0),
          ot_idv: (r.ot_idv_count ?? 0),
          ot_order: (r.ot_order_count ?? 0),
          total_awards: r.total_awards,
          child_count: r.child_count,
        }))
    }

    // ot_prevalence
    return filtered
      .sort((a, b) => (b.total_ot_awards ?? 0) - (a.total_ot_awards ?? 0))
      .slice(0, 20)
      .map(r => ({
        code: r.entity_code,
        label: r.entity_name || r.entity_code,
        ot_agreement: (r.ot_agreement_count ?? 0),
        ot_idv: (r.ot_idv_count ?? 0),
        ot_order: (r.ot_order_count ?? 0),
        far: (r.total_far_awards ?? 0),
        ot_pct: r.ot_award_pct != null ? Math.round(r.ot_award_pct * 1000) / 10 : 0,
        ot_obligated: r.ot_obligated,
        total_awards: r.total_awards,
        child_count: r.child_count,
      }))
  }, [data, metric])

  const drillLabel = nextLevel ? LEVEL_LABELS[nextLevel] : LEVEL_LABELS[queryLevel]
  const chartHeight = Math.max(400, chartItems.length * 36)

  // Metric-specific tooltip
  const renderTooltip = useCallback(({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    const row = chartItems.find(d => d.label === label)
    if (!row) return null

    return (
      <div className="bg-white border border-gray-200 shadow-lg rounded-lg px-3 py-2 text-xs space-y-1">
        <div className="font-semibold text-gray-900 truncate max-w-[250px]">{label}</div>
        {metric === 'speed' && (
          <>
            <div className="text-blue-600">{(row as any).value} days avg</div>
            <div className="text-gray-400">{formatNumber(row.total_awards)} awards</div>
          </>
        )}
        {metric === 'small_biz' && (
          <>
            <div className="text-green-600">{(row as any).value}% small business</div>
            <div className="text-gray-400">{formatNumber((row as any).small_biz_awards)} SB awards of {formatNumber(row.total_awards)}</div>
          </>
        )}
        {metric === 'vehicles' && (
          <>
            {payload.map((p: any) => (
              <div key={p.dataKey} style={{ color: p.fill }}>{p.name}: {formatNumber(p.value)}</div>
            ))}
            <div className="mt-1 pt-1 border-t border-gray-100 text-gray-400">
              {formatNumber(row.total_awards)} total awards
            </div>
          </>
        )}
        {metric === 'ot_prevalence' && (
          <>
            <div className="text-purple-700">OT Agreements: {formatNumber((row as any).ot_agreement)}</div>
            <div className="text-purple-500">OT IDVs: {formatNumber((row as any).ot_idv)}</div>
            <div className="text-pink-500">OT Orders: {formatNumber((row as any).ot_order)}</div>
            <div className="text-gray-500">FAR Awards: {formatNumber((row as any).far)}</div>
            <div className="mt-1 pt-1 border-t border-gray-100">
              <span className="font-semibold text-purple-700">{(row as any).ot_pct}% OT</span>
              <span className="text-gray-400 ml-2">{formatDollars((row as any).ot_obligated)} OT obligated</span>
            </div>
          </>
        )}
      </div>
    )
  }, [chartItems, metric])

  return (
    <SectionCard
      title={config.title}
      subtitle={config.subtitle(drillLabel)}
      loading={loading}
      error={error}
    >
      {/* Breadcrumb */}
      {breadcrumbs.length > 1 && (
        <div className="flex items-center gap-1.5 text-sm mb-4">
          {breadcrumbs.map((bc, idx) => (
            <span key={idx} className="flex items-center gap-1.5">
              {idx > 0 && <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
              {idx < breadcrumbs.length - 1 ? (
                <button
                  onClick={() => handleBreadcrumbClick(idx)}
                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline whitespace-nowrap"
                >
                  {bc.name}
                </button>
              ) : (
                <span className="font-medium text-gray-900 whitespace-nowrap">{bc.name}</span>
              )}
            </span>
          ))}
        </div>
      )}

      {chartItems.length === 0 ? (
        <div className="h-80 flex items-center justify-center text-sm text-gray-400">
          {loading ? '' : 'No data at this level'}
        </div>
      ) : (
        <div style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartItems}
              layout="vertical"
              margin={{ top: 5, right: 20, bottom: 5, left: 5 }}
              barSize={18}
            >
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f1f1" />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: '#6b7280' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => {
                  if (metric === 'small_biz') return `${v}%`
                  if (metric === 'speed') return `${v}`
                  return v.toLocaleString()
                }}
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 9, fill: '#6b7280' }}
                tickLine={false}
                axisLine={false}
                width={170}
                tickFormatter={(v: string) => v.length > 26 ? v.slice(0, 24) + '\u2026' : v}
              />
              <Tooltip content={renderTooltip} />

              {/* Speed: single value bar */}
              {metric === 'speed' && (
                <Bar dataKey="value" name="Avg Days to Award" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick}>
                  {chartItems.map((_, i) => (
                    <Cell key={i} fill={ENTITY_COLORS[i % ENTITY_COLORS.length]} />
                  ))}
                </Bar>
              )}

              {/* Small Biz: single value bar */}
              {metric === 'small_biz' && (
                <Bar dataKey="value" name="Small Business %" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick}>
                  {chartItems.map((_, i) => (
                    <Cell key={i} fill={ENTITY_COLORS[i % ENTITY_COLORS.length]} />
                  ))}
                </Bar>
              )}

              {/* Vehicles: stacked bar */}
              {metric === 'vehicles' && (
                <>
                  <Legend />
                  <Bar dataKey="gwac_idiq" stackId="a" fill={VEHICLE_COLORS['GWAC/IDIQ']} name="GWAC/IDIQ" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick} />
                  <Bar dataKey="bpa" stackId="a" fill={VEHICLE_COLORS['BPA']} name="BPA" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick} />
                  <Bar dataKey="standalone" stackId="a" fill={VEHICLE_COLORS['Standalone']} name="Standalone" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick} />
                  <Bar dataKey="ot_agreement" stackId="a" fill={VEHICLE_COLORS['OT Agreement']} name="OT Agreement" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick} />
                  <Bar dataKey="ot_idv" stackId="a" fill={VEHICLE_COLORS['OT IDV']} name="OT IDV" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick} />
                  <Bar dataKey="ot_order" stackId="a" fill={VEHICLE_COLORS['OT Order']} name="OT Order" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick} />
                </>
              )}

              {/* OT Prevalence: stacked bar */}
              {metric === 'ot_prevalence' && (
                <>
                  <Legend />
                  <Bar dataKey="ot_agreement" stackId="a" fill={OT_COLORS.ot_agreement} name="OT Agreement" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick} />
                  <Bar dataKey="ot_idv" stackId="a" fill={OT_COLORS.ot_idv} name="OT IDV" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick} />
                  <Bar dataKey="ot_order" stackId="a" fill={OT_COLORS.ot_order} name="OT Order" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick} />
                  <Bar dataKey="far" stackId="a" fill={OT_COLORS.far} name="FAR Awards" cursor={canDrill ? 'pointer' : 'default'} onClick={handleBarClick} />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </SectionCard>
  )
}
